<template>
  <div class="customer-note-content">
    <div v-if="loading" v-loading="loading" style="min-height: 200px;"></div>

    <template v-else>
      <!-- 新增備註 -->
      <div class="add-note-section">
        <el-input v-model="newNote" type="textarea" :rows="4" placeholder="請輸入客戶備註..." class="note-input" />
        <el-button type="primary" round :loading="saving" :disabled="!newNote.trim()" @click="handleAddNote" style="margin-top: 12px;"> <i class="el-icon-plus"></i> 新增備註 </el-button>
      </div>

      <el-divider></el-divider>

      <!-- 備註列表 -->
      <div class="note-list">
        <div class="list-header"> <h4 class="list-title">歷史備註</h4><span class="note-subtitle">(客戶無法查看)</span> </div>

        <div v-if="notes.length === 0" class="empty-state">
          <i class="el-icon-chat-line-square" style="font-size: 48px; color: #C0C4CC;"></i>
          <p>暫無備註紀錄</p>
        </div>

        <div v-else>
          <div v-for="note in sortedNotes" :key="note.id" class="note-card">
            <div class="note-header">
              <span class="note-author">{{ note.creator ? note.creator.name : '系統' }}</span>
              <div class="note-actions">
                <el-button type="text" size="small" @click="handleEditNote(note)" style="color: #409EFF; padding: 5px 8px;">編輯</el-button>
                <el-button type="text" size="small" @click="handleDeleteNote(note)" style="color: #F56C6C; padding: 5px 8px;">刪除</el-button>
              </div>
            </div>
            <div class="note-date">{{ note.createdAt }}</div>
            <div v-if="editingNoteId === note.id" class="note-edit">
              <el-input v-model="editingContent" type="textarea" :rows="3" />
              <div class="edit-actions">
                <el-button round size="small" @click="cancelEdit">取消</el-button>
                <el-button round type="primary" size="small" :loading="saving" @click="saveEdit(note)">儲存</el-button>
              </div>
            </div>
            <div v-else class="note-body">
              {{ note.content }}
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'CustomerNoteContent',
  props: {
    memberId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      saving: false,
      newNote: '',
      notes: [],
      editingNoteId: null,
      editingContent: '',
    }
  },
  computed: {
    sortedNotes() {
      // 按創建時間降序排序（新 >>> 舊）
      return [...this.notes].sort((a, b) => {
        const timeA = new Date(a.createdAt).getTime()
        const timeB = new Date(b.createdAt).getTime()
        return timeB - timeA
      })
    },
  },
  created() {
    this.fetchNotes()
  },
  methods: {
    async fetchNotes() {
      if (!this.memberId) return
      this.loading = true
      try {
        this.notes = await this.$store.dispatch('comment/fetchList', {
          memberId: this.memberId,
          type: 'member_private',
          paginate: 0,
        })
      } catch (error) {
        console.error('取得備註失敗', error)
        this.notes = []
      } finally {
        this.loading = false
      }
    },
    async handleAddNote() {
      if (!this.newNote.trim()) return
      this.saving = true
      try {
        // 使用 comment/store API 新增備註
        await this.$store.dispatch('comment/store', {
          type: 'member_private',
          memberId: this.memberId,
          content: this.newNote.trim(),
        })
        this.$message.success('備註新增成功')
        this.newNote = ''
        await this.fetchNotes()
      } catch (error) {
        console.error('新增備註失敗', error)
        this.$message.error('新增備註失敗')
      } finally {
        this.saving = false
      }
    },
    handleEditNote(note) {
      this.editingNoteId = note.id
      this.editingContent = note.content
    },
    cancelEdit() {
      this.editingNoteId = null
      this.editingContent = ''
    },
    async saveEdit(note) {
      if (!this.editingContent.trim()) {
        this.$message.warning('備註內容不能為空')
        return
      }
      this.saving = true
      try {
        // 使用 comment/store API 更新備註（uuid 存在時為更新）
        await this.$store.dispatch('comment/store', {
          uuid: note.id,
          type: 'member_private',
          memberId: this.memberId,
          content: this.editingContent.trim(),
        })
        this.$message.success('備註更新成功')
        this.cancelEdit()
        await this.fetchNotes()
      } catch (error) {
        console.error('更新備註失敗', error)
        this.$message.error('更新備註失敗')
      } finally {
        this.saving = false
      }
    },
    handleDeleteNote(note) {
      this.$confirm('確定要刪除這則備註嗎？', '刪除確認', {
        confirmButtonText: '確定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(async () => {
          try {
            await this.$store.dispatch('comment/removeComment', {
              ids: [note.id],
            })
            this.$message.success('備註刪除成功')
            await this.fetchNotes()
          } catch (error) {
            console.error('刪除備註失敗', error)
            this.$message.error('刪除備註失敗')
          }
        })
        .catch(() => {
          // 取消刪除
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.customer-note-content {
  .add-note-section {
    .note-input {
      ::v-deep .el-textarea__inner {
        border-radius: 8px;
        border-color: #e5e7eb;

        &:focus {
          border-color: #ea6a2a;
        }
      }
    }
  }

  .note-list {
    .list-title {
      font-size: 16px;
      font-weight: 600;
      color: #121931;
    }
    .note-subtitle {
      font-size: 12px;
      color: #909399;
      margin-left: 4px;
    }

    .list-header {
      display: flex;
      align-items: center;
      justify-content: left;
      margin-bottom: 8px;
    }

    .empty-state {
      text-align: center;
      padding: 40px 0;
      color: #909399;
    }

    .note-card {
      background: #fff;
      border-radius: 8px;
      padding: 16px;
      margin-bottom: 12px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

      .note-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 4px;

        .note-author {
          font-weight: 600;
          color: #121931;
        }

        .note-actions {
          display: flex;
          align-items: center;
          gap: 4px;
        }
      }

      .note-date {
        font-size: 12px;
        color: #909399;
        margin-bottom: 12px;
        padding-bottom: 12px;
        border-bottom: 1px solid #f2f2f2;
      }

      .note-body {
        color: #606266;
        white-space: pre-line;
      }

      .note-edit {
        .edit-actions {
          display: flex;
          justify-content: flex-end;
          gap: 8px;
          margin-top: 10px;
        }

        ::v-deep .el-textarea__inner {
          border-radius: 8px;
          border-color: #e5e7eb;

          &:focus {
            border-color: #ea6a2a;
          }
        }
      }
    }
  }
}
</style>
